import { ChangeDetectionStrategy, Component, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AuthService, ExternalLink, LicenseService, LinksService } from '@isophi/core-legacy';
import { Material } from '@isophi/teachers-shared';
import { TranslocoService } from '@jsverse/transloco';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

import { RouterLinkFactory } from '../../../../core/router/router-link.factory';
import { KindergartenService } from '../../../../core/services/kindergarten.service';
import { ProductType } from '../../../child/components/list-filter/list-filter.component';
import { MaterialService } from '../../services/material.service';
import { DiagnosticVideoComponent } from '../diagnostic-video/diagnostic-video.component';

interface NavItem {
  headline: string;
  description: string;
  iconPath: string;
  secondIconPath: string;
  link: string | (string | number)[];
  isExternalLink?: boolean;
  target: string;
  disabled?: boolean;
  click?: boolean;
  onClick?: () => void;
}

@Component({
    selector: 'app-visual-navigation',
    templateUrl: './visual-navigation.component.html',
    styleUrls: ['./visual-navigation.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class VisualNavigationComponent implements OnInit, OnDestroy {
  @Input() dashboardSK = false;

  @Input() dashboardSchool = false;

  @Input() evalCalculator = false;

  @Input() managementTrial = false;

  @Input() managementAccess = false;

  @Input() isDemo = false;

  @Input() childPortfolio = false;

  @Input() testKit = false;

  @ViewChild(DiagnosticVideoComponent)
  public popUpComponent: DiagnosticVideoComponent;

  navItems$ = new BehaviorSubject<NavItem[] | null>(null);

  readonly productType = ProductType;

  private readonly destroy$ = new Subject<void>();

  private diagnosticMaterials: Material[][] = [];

  private readonly authService = inject(AuthService);

  private readonly routerLinkFactory = inject(RouterLinkFactory);

  private readonly transloco = inject(TranslocoService);

  private readonly materialService = inject(MaterialService);

  private readonly kindergartenService = inject(KindergartenService);

  private readonly licenceService = inject(LicenseService);

  private readonly linksService = inject(LinksService);

  ngOnInit(): void {
    this.transloco.langChanges$.pipe(takeUntil(this.destroy$)).subscribe(async (lang) => {
      await this.transloco.load(lang).toPromise();
      this.updateNavigationItems();
    });

    this.materialService.diagnosticMaterials$.pipe(takeUntil(this.destroy$)).subscribe((materials) => {
      this.diagnosticMaterials = materials;
      this.updateNavigationItems();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isOldDashboard(): boolean {
    return this.dashboardSK || this.dashboardSchool;
  }

  private updateNavigationItems(): void {
    if (!this.diagnosticMaterials) {
      return;
    }

    const items = this.isOldDashboard ? this.getOldDashboardItems() : this.getNewDashboardItems();

    items.pipe(take(1)).subscribe((newItems) => {
      this.navItems$.next(newItems);
    });
  }

  private getOldDashboardItems(): Observable<NavItem[]> {
    const hasDiagnosticMaterials = this.diagnosticMaterials.length > 0;
    const lastCard = this.evalCalculator ? this.cardCalculator() : this.cardParentsApp();

    return of([
      this.cardDiagnostics(),
      this.cardChildrenAndResults(),
      this.cardGroupResults(),
      this.cardTutorials(),
      this.cardMaterials(hasDiagnosticMaterials),
      lastCard,
    ]);
  }

  private getNewDashboardItems(): Observable<NavItem[]> {
    return combineLatest([
      this.licenceService.canSeeChildPortfolio,
      this.licenceService.canSeeManagement,
      this.licenceService.canSeeTeacherApp,
      this.licenceService.canSeePap,
    ]).pipe(
      map(([showChildPortfolio, showManagement, showTeacherApp, showPap]) => {
        const items: NavItem[] = [];

        if (showTeacherApp) {
          items.push(this.cardDiagnostics());
        }
        if (showChildPortfolio) {
          items.push(this.cardChildPortfolio());
        }
        if (showPap) {
          items.push(this.cardPAP());
        }
        if (showManagement) {
          items.push(this.cardManagement());
        }
        return items;
      })
    );
  }

  private cardChildrenAndResults(): NavItem {
    return {
      headline: this.transloco.translate('child/results'),
      description: this.transloco.translate('child/resultsText'),
      iconPath: 'assets/img/iconsColor/girl.png',
      secondIconPath: '',
      link: this.routerLinkFactory.childList(),
      target: '_self',
    };
  }

  private cardGroupResults(): NavItem {
    return {
      headline: this.transloco.translate('child/evaluation'),
      description: this.transloco.translate('child/evaluationGroupText'),
      iconPath: 'assets/img/iconsColor/childrenGreen.png',
      secondIconPath: '',
      link: this.routerLinkFactory.groupReportProblematicCategories(),
      target: '_self',
    };
  }

  private cardMaterials(eduAndDiagnostic: boolean): NavItem {
    return {
      headline: this.transloco.translate('shared/materials'),
      description: this.transloco.translate('shared/materialsText'),
      iconPath: 'assets/img/iconsColor/tests.png',
      secondIconPath: '',
      link: eduAndDiagnostic ? this.routerLinkFactory.materialsDiagnostic() : this.routerLinkFactory.materialsEducational(),
      target: '_self',
    };
  }

  private cardTutorials(): NavItem {
    return {
      headline: this.transloco.translate('support/manualsAndInstructions'),
      description: this.transloco.translate('support/manualsAndInstructionsText'),
      iconPath: 'assets/img/iconsColor/manual.png',
      secondIconPath: '',
      link: this.routerLinkFactory.manuals(),
      target: '_self',
    };
  }

  private cardDiagnostics(): NavItem {
    const dadUrl = this.linksService.getDadAppUrl({ redirect: '/children' });
    return {
      headline: this.transloco.translate('shared/digital'),
      description: this.transloco.translate('shared/diagText'),
      iconPath: this.isOldDashboard ? 'assets/img/iconsColor/diagnostic.png' : 'assets/img/iconsColor/diagnostics.svg',
      secondIconPath: '',
      link: dadUrl.url,
      isExternalLink: true,
      target: dadUrl.target,
      click: this.isDemo || (this.kindergartenService.loggedKindergarten.isCZKindergarten() && !this.testKit),
      onClick: () => {
        this.popUpComponent.showPopup();
      },
    };
  }

  private cardChildPortfolio(): NavItem {
    const managementUrl = this.linksService.getManagementAppUrl({
      redirect: '/children-portfolio/dashboard',
      queryParams: { source: 'tapp' },
    });
    return {
      headline: this.transloco.translate('shared/childPortfolio'),
      description: this.transloco.translate('shared/portfolioText'),
      iconPath: this.isOldDashboard ? 'assets/img/iconsColor/childrenGreen.png' : 'assets/img/iconsColor/children-portfolio.svg',
      secondIconPath: '',
      link: managementUrl.url,
      target: managementUrl.target,
      isExternalLink: true,
    };
  }

  private cardPAP(): NavItem {
    let url: ExternalLink | null = null;

    if (
      this.kindergartenService.loggedKindergarten.isKindergarten() &&
      (this.authService.loggedUser.teacher.isManager() || this.authService.loggedUser.teacher.isDirector())
    ) {
      url = this.linksService.getManagementAppUrl({ redirect: '/pap/portfolio', queryParams: { source: 'tapp' } });
    } else {
      url = this.linksService.getPapAppUrl({ redirect: '/info/dashboard' });
    }
    return {
      headline: this.transloco.translate('shared/papApp'),
      description: this.transloco.translate('shared/papAppText'),
      iconPath: this.isOldDashboard ? 'assets/img/iconsColor/brain.png' : 'assets/img/iconsColor/pap.svg',
      secondIconPath: '',
      link: url.url,
      target: url.target,
      isExternalLink: true,
    };
  }

  private cardManagement(): NavItem {
    const managementUrl = this.linksService.getManagementAppUrl({ redirect: '/management/dashboard', queryParams: { source: 'tapp' } });

    return {
      headline: this.transloco.translate('management/title'),
      description: this.transloco.translate('management/description'),
      iconPath: this.isOldDashboard ? 'assets/img/iconsColor/kindergarten.png' : 'assets/img/iconsColor/management.svg',
      secondIconPath: '',
      link: managementUrl.url,
      target: managementUrl.target,
      isExternalLink: true,
    };
  }

  private cardParentsApp(): NavItem {
    return {
      headline: this.transloco.translate('result/overview'),
      description: this.transloco.translate('result/overviewText'),
      iconPath: 'assets/img/iconsColor/iconR.png',
      secondIconPath: 'assets/img/iconsDefault/linesGray.png',
      link: this.routerLinkFactory.childList(),
      target: '_self',
    };
  }

  private cardGames(): NavItem {
    return {
      headline: this.transloco.translate('games/playfulDev'),
      description: this.transloco.translate('games/playfulDevText'),
      iconPath: 'assets/img/iconsColor/puzzle.png',
      secondIconPath: 'assets/img/iconsDefault/game.png',
      link: this.routerLinkFactory.games(),
      target: '_self',
    };
  }

  private cardCalculator(): NavItem {
    return {
      headline: this.transloco.translate('calculator/evaluationZone'),
      description: this.transloco.translate('calculator/evaluationZoneText'),
      iconPath: 'assets/img/iconsColor/calculator.png',
      secondIconPath: '',
      link: `https://isophi.sk/kalkulacka/`,
      isExternalLink: true,
      target: 'calculator',
    };
  }
}
